import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { PopupHelpButton } from "../Help/PopupHelpButton"

interface MetricCardProps {
  title: string
  value: string
  subtitle?: string
  icon: IconProp
  color?: string
  animationDelay?: string
  helpDescription?: string
  helpBullets?: string[]
  onClick?: () => void
  ariaLabel?: string
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = "#ea580c",
  animationDelay = "0s",
  helpDescription,
  helpBullets,
  onClick,
  ariaLabel,
}) => {
  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="2s" delay={animationDelay}>
      <div
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={ariaLabel}
        title={onClick ? ariaLabel : undefined}
        onClick={onClick}
        onKeyDown={(event) => {
          if (!onClick || (event.key !== "Enter" && event.key !== " ")) return

          event.preventDefault()
          onClick()
        }}
        className={`
          rounded-xl
          px-4 py-4
          shadow-sm
          hover:shadow-md hover:-translate-y-0.5
          transition-all duration-300
          flex flex-col justify-between
          min-h-[110px]
          bg-gradient-to-br from-white via-orange-50/40 to-white
          border-2 border-orange-200
          relative overflow-hidden
          ${onClick ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2" : ""}
        `}
        data-interaction-description-key="interactionDescriptions:components.metricCard.metricCard.button.onClickAriaLabelUndefined"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

        <div className="flex justify-between items-start gap-3">
          <div className="flex-1">
            <span className="text-[0.78rem] font-bold text-slate-500 uppercase tracking-[0.12em]">{title}</span>
          </div>

          <div className="flex items-center gap-2">
            {helpDescription && (
              <PopupHelpButton
                helpKey="components.metriccard.metriccard.1"
                title={title}
                description={helpDescription}
                bullets={helpBullets ?? []}
              />
            )}

            <div
              className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-200 bg-white text-[18px] shadow-sm"
              style={{ color }}
            >
              <FontAwesomeIcon icon={icon} />
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-col">
          <span className="text-[1.35rem] font-black text-slate-900 leading-tight">{value}</span>

          {subtitle && <span className="mt-2 text-[0.75rem] font-semibold leading-5 text-slate-500">{subtitle}</span>}
        </div>
      </div>
    </Animate>
  )
}
